import React from 'react';
import SEO, { siteUrl } from '../components/SEO';

const sections = [
  {
    title: 'Information We Collect',
    body: 'We may collect certain information automatically through third-party services used in the game, including:',
    items: [
      'Device information',
      'Operating system version',
      'Advertising identifiers',
      'App usage and performance data',
      'Crash reports and diagnostics'
    ]
  },
  {
    title: 'Advertising',
    body: 'Office Outlaws uses Google AdMob to display advertisements. AdMob may collect and process data to provide personalized and non-personalized ads.',
    link: {
      label: 'Google Privacy Policy',
      href: 'https://policies.google.com/privacy'
    }
  },
  {
    title: 'Analytics and Diagnostics',
    body: 'We may use third-party services to analyze app performance, diagnose crashes, and improve user experience.'
  },
  {
    title: "Children's Privacy",
    body: 'Office Outlaws is not directed toward children under the age of 13. We do not knowingly collect personal information from children.'
  },
  {
    title: 'Data Security',
    body: 'We take reasonable measures to protect information collected through the app. However, no method of electronic storage or transmission is completely secure.'
  },
  {
    title: 'Third-Party Services',
    body: 'The game may use third-party services including:',
    items: ['Google Play Services', 'Google AdMob'],
    after: 'These services may collect information in accordance with their own privacy policies.'
  },
  {
    title: 'Changes to This Privacy Policy',
    body: 'We may update this Privacy Policy from time to time. Any changes will be reflected on this page with an updated revision date.'
  }
];

const OfficeOutlawsPrivacyPolicy: React.FC = () => {
  const policyJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Office Outlaws Privacy Policy',
    url: `${siteUrl}/office-outlaws-privacy-policy`,
    dateModified: '2026-06',
    publisher: {
      '@type': 'Organization',
      name: 'RedRender',
      url: siteUrl
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-black via-gray-900 to-black pt-24 pb-20">
      <SEO
        title="Office Outlaws Privacy Policy - RedRender"
        description="Privacy Policy for Office Outlaws, covering advertising, analytics, diagnostics, children's privacy, data security, and third-party services."
        path="/office-outlaws-privacy-policy"
        keywords={[
          'Office Outlaws privacy policy',
          'Office Outlaws',
          'mobile game privacy policy',
          'RedRender privacy policy'
        ]}
        jsonLd={policyJsonLd}
      />

      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.012)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.012)_1px,transparent_1px)] bg-[size:72px_72px]" />
      <div className="pointer-events-none absolute left-[-8rem] top-28 h-80 w-80 rounded-full bg-red-500/15 blur-3xl" />
      <div className="pointer-events-none absolute right-[-8rem] top-[30rem] h-96 w-96 rounded-full bg-red-600/10 blur-3xl" />

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="mb-10 animate-slide-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                </span>
                <span className="font-display text-sm font-semibold text-red-400">Office Outlaws</span>
              </div>

              <h1 className="mt-8 font-display text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
                Privacy Policy
              </h1>
              <p className="mt-4 text-lg text-gray-400">Last Updated: June 2026</p>
            </div>

            <article className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-6 shadow-2xl shadow-black/25 backdrop-blur-xl sm:p-10">
              <p className="text-lg leading-relaxed text-gray-300">
                Office Outlaws ("we", "our", or "us") respects your privacy. This Privacy Policy explains how
                information may be collected, used, and shared when you use our mobile game, Office Outlaws.
              </p>

              <div className="mt-10 space-y-8">
                {sections.map((section) => (
                  <section key={section.title} className="border-t border-white/10 pt-8">
                    <h2 className="font-display text-2xl font-bold text-white">{section.title}</h2>
                    <p className="mt-4 leading-relaxed text-gray-300">{section.body}</p>

                    {section.items && (
                      <ul className="mt-5 space-y-3">
                        {section.items.map((item) => (
                          <li key={item} className="flex gap-3 text-gray-300">
                            <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {section.link && (
                      <a
                        href={section.link.href}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-5 inline-flex text-red-400 transition-colors hover:text-red-300"
                      >
                        {section.link.label}
                      </a>
                    )}

                    {section.after && <p className="mt-5 leading-relaxed text-gray-300">{section.after}</p>}
                  </section>
                ))}

                <section className="border-t border-white/10 pt-8">
                  <h2 className="font-display text-2xl font-bold text-white">Contact Us</h2>
                  <p className="mt-4 leading-relaxed text-gray-300">
                    If you have any questions regarding this Privacy Policy, you may contact us at:
                  </p>
                  <a
                    href="mailto:info@redrender.in"
                    className="mt-5 inline-flex rounded-full border border-red-500/30 bg-red-500/10 px-5 py-3 font-semibold text-red-300 transition-colors hover:border-red-400/60 hover:text-white"
                  >
                    info@redrender.in
                  </a>
                </section>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OfficeOutlawsPrivacyPolicy;
